import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Star, ChevronLeft, ChevronRight, ImagePlus, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return `${BACKEND_URL}${data.url}`;
}

function Dropzone({ onFiles, multiple, busy, label }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const handle = (files) => { if (files && files.length) onFiles(Array.from(files)); };
  return (
    <div
      onClick={() => ref.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      data-testid="image-dropzone"
      className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${drag ? "border-forest bg-sage/10" : "border-sage/50 bg-sand hover:bg-sage/10"}`}
    >
      {busy ? <Loader2 className="w-5 h-5 animate-spin text-forest mx-auto" /> : <ImagePlus className="w-6 h-6 text-olive mx-auto mb-1" />}
      <p className="text-sm text-brown font-500">{label || "Upload image"}</p>
      <p className="text-xs text-brown/60">Drag & drop or click to browse — JPG, PNG, WEBP</p>
      <input ref={ref} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple={multiple} className="hidden"
        onChange={(e) => { handle(e.target.files); e.target.value = ""; }} />
    </div>
  );
}

export function ImageUploader({ value, onChange, label }) {
  const [busy, setBusy] = useState(false);
  const onFiles = async (files) => {
    setBusy(true);
    try { onChange(await uploadFile(files[0])); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  if (value) {
    return (
      <div className="relative inline-block group">
        <img src={value} alt="preview" className="h-28 w-28 rounded-xl object-cover border border-sage/30" data-testid="uploader-preview" />
        <div className="absolute inset-0 rounded-xl bg-forest/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <label className="w-8 h-8 rounded-full bg-white flex items-center justify-center cursor-pointer" title="Replace" data-testid="uploader-replace">
            {busy ? <Loader2 className="w-4 h-4 animate-spin text-forest" /> : <Upload className="w-4 h-4 text-forest" />}
            <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={(e) => { if (e.target.files[0]) onFiles([e.target.files[0]]); e.target.value = ""; }} />
          </label>
          <button type="button" onClick={() => onChange("")} title="Delete" data-testid="uploader-delete" className="w-8 h-8 rounded-full bg-white flex items-center justify-center"><X className="w-4 h-4 text-terracotta" /></button>
        </div>
      </div>
    );
  }
  return <Dropzone onFiles={onFiles} busy={busy} label={label} />;
}

export function GalleryUploader({ images = [], onChange }) {
  const [busy, setBusy] = useState(false);
  const onFiles = async (files) => {
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadFile(f));
      onChange([...(images || []), ...urls]);
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} added`);
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  const move = (i, dir) => {
    const arr = [...images]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; onChange(arr);
  };
  const setPrimary = (i) => { const arr = [...images]; const [x] = arr.splice(i, 1); arr.unshift(x); onChange(arr); };
  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      <Dropzone onFiles={onFiles} multiple busy={busy} label="Upload product images" />
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="gallery-grid">
          {images.map((img, i) => (
            <div key={i} className="relative rounded-xl overflow-hidden border border-sage/30 bg-sand group" data-testid={`gallery-item-${i}`}>
              <img src={img} alt="" className="w-full h-24 object-cover" />
              {i === 0 && <span className="absolute top-1 left-1 bg-forest text-cream text-[10px] px-1.5 py-0.5 rounded-full">Primary</span>}
              <div className="absolute inset-x-0 bottom-0 bg-forest/70 opacity-0 group-hover:opacity-100 transition-opacity flex justify-center gap-1 py-1">
                <button type="button" onClick={() => move(i, -1)} className="text-cream p-1"><ChevronLeft className="w-3.5 h-3.5" /></button>
                {i !== 0 && <button type="button" onClick={() => setPrimary(i)} title="Set primary" data-testid={`gallery-primary-${i}`} className="text-mustard p-1"><Star className="w-3.5 h-3.5" /></button>}
                <button type="button" onClick={() => move(i, 1)} className="text-cream p-1"><ChevronRight className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => remove(i)} data-testid={`gallery-remove-${i}`} className="text-terracotta bg-white/90 rounded-full p-1"><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
